import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Avatar,
  Badge,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Support,
  Assignment,
  VerifiedUser,
  Notifications,
  Add,
  Reply,
  Close,
  CheckCircle,
  Warning,
  Error,
  Info,
  PriorityHigh,
  Schedule,
  Person,
  Business,
  Email,
  Phone,
  Description,
  AttachFile,
  Download,
  Visibility,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`support-tabpanel-${index}`}
      aria-labelledby={`support-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Ticket {
  id: number;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'technical' | 'billing' | 'dispute' | 'question' | 'other';
  requester: string;
  requesterEmail: string;
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  messages: Array<{
    id: number;
    sender: string;
    message: string;
    timestamp: string;
    isAdmin: boolean;
  }>;
}

interface KYCRequest {
  id: number;
  userId: string;
  userName: string;
  userEmail: string;
  documentType: 'id_card' | 'passport' | 'driving_license' | 'utility_bill';
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
  reviewedAt?: string;
  reviewer?: string;
  notes?: string;
}

interface Notification {
  id: number;
  type: 'email' | 'sms' | 'push' | 'in_app';
  recipient: string;
  subject: string;
  content: string;
  status: 'sent' | 'delivered' | 'failed' | 'pending';
  sentAt: string;
  deliveredAt?: string;
}

const SupportScreen: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [newTicketDialog, setNewTicketDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [replyDialog, setReplyDialog] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');

  // Mock data
  const [tickets, setTickets] = useState<Ticket[]>([
    {
      id: 1,
      title: 'Problème de connexion à l\'application',
      description: 'Je n\'arrive pas à me connecter depuis hier soir. L\'erreur indique "Invalid credentials".',
      status: 'open',
      priority: 'high',
      category: 'technical',
      requester: 'Jean Dupont',
      requesterEmail: 'jean.dupont@email.com',
      createdAt: '2024-01-15T10:30:00Z',
      updatedAt: '2024-01-15T10:30:00Z',
      messages: [
        {
          id: 1,
          sender: 'Jean Dupont',
          message: 'Je n\'arrive pas à me connecter depuis hier soir. L\'erreur indique "Invalid credentials".',
          timestamp: '2024-01-15T10:30:00Z',
          isAdmin: false,
        },
      ],
    },
    {
      id: 2,
      title: 'Dispute sur une commission',
      description: 'Je conteste le montant de la commission reçue pour la vente de la propriété rue de la Paix.',
      status: 'in_progress',
      priority: 'urgent',
      category: 'dispute',
      requester: 'Marie Martin',
      requesterEmail: 'marie.martin@email.com',
      createdAt: '2024-01-14T14:20:00Z',
      updatedAt: '2024-01-15T09:15:00Z',
      assignedTo: 'Admin Support',
      messages: [
        {
          id: 1,
          sender: 'Marie Martin',
          message: 'Je conteste le montant de la commission reçue pour la vente de la propriété rue de la Paix.',
          timestamp: '2024-01-14T14:20:00Z',
          isAdmin: false,
        },
        {
          id: 2,
          sender: 'Admin Support',
          message: 'Bonjour Marie, nous examinons votre dossier. Pouvez-vous nous fournir plus de détails ?',
          timestamp: '2024-01-15T09:15:00Z',
          isAdmin: true,
        },
      ],
    },
    {
      id: 3,
      title: 'Question sur les taux de commission',
      description: 'Quels sont les taux de commission applicables pour les ventes en région parisienne ?',
      status: 'resolved',
      priority: 'medium',
      category: 'question',
      requester: 'Pierre Durand',
      requesterEmail: 'pierre.durand@email.com',
      createdAt: '2024-01-13T16:45:00Z',
      updatedAt: '2024-01-14T11:30:00Z',
      assignedTo: 'Admin Support',
      messages: [
        {
          id: 1,
          sender: 'Pierre Durand',
          message: 'Quels sont les taux de commission applicables pour les ventes en région parisienne ?',
          timestamp: '2024-01-13T16:45:00Z',
          isAdmin: false,
        },
        {
          id: 2,
          sender: 'Admin Support',
          message: 'Bonjour Pierre, les taux de commission en région parisienne sont de 5% pour les ventes jusqu\'à 500k€ et 4% au-delà.',
          timestamp: '2024-01-14T11:30:00Z',
          isAdmin: true,
        },
        {
          id: 3,
          sender: 'Pierre Durand',
          message: 'Parfait, merci pour cette précision !',
          timestamp: '2024-01-14T14:20:00Z',
          isAdmin: false,
        },
      ],
    },
  ]);

  const [kycRequests, setKycRequests] = useState<KYCRequest[]>([
    {
      id: 1,
      userId: 'user001',
      userName: 'Sophie Bernard',
      userEmail: 'sophie.bernard@email.com',
      documentType: 'id_card',
      status: 'pending',
      submittedAt: '2024-01-15T08:00:00Z',
    },
    {
      id: 2,
      userId: 'user002',
      userName: 'Lucas Moreau',
      userEmail: 'lucas.moreau@email.com',
      documentType: 'passport',
      status: 'approved',
      submittedAt: '2024-01-14T12:30:00Z',
      reviewedAt: '2024-01-15T10:00:00Z',
      reviewer: 'Admin Support',
    },
    {
      id: 3,
      userId: 'user003',
      userName: 'Emma Petit',
      userEmail: 'emma.petit@email.com',
      documentType: 'utility_bill',
      status: 'rejected',
      submittedAt: '2024-01-13T15:45:00Z',
      reviewedAt: '2024-01-14T16:20:00Z',
      reviewer: 'Admin Support',
      notes: 'Document illisible, veuillez soumettre une version plus claire',
    },
  ]);

  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 1,
      type: 'email',
      recipient: 'jean.dupont@email.com',
      subject: 'Bienvenue sur notre plateforme',
      content: 'Votre compte a été créé avec succès.',
      status: 'delivered',
      sentAt: '2024-01-15T09:00:00Z',
      deliveredAt: '2024-01-15T09:02:00Z',
    },
    {
      id: 2,
      type: 'sms',
      recipient: '+33123456789',
      subject: 'Nouveau lead disponible',
      content: 'Un nouveau lead est disponible dans votre zone.',
      status: 'sent',
      sentAt: '2024-01-15T08:30:00Z',
    },
    {
      id: 3,
      type: 'push',
      recipient: 'user_device_001',
      subject: 'Commission validée',
      content: 'Votre commission de 500€ a été validée.',
      status: 'delivered',
      sentAt: '2024-01-15T07:45:00Z',
      deliveredAt: '2024-01-15T07:46:00Z',
    },
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'error';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'default';
      case 'medium': return 'info';
      case 'high': return 'warning';
      case 'urgent': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Ouvert';
      case 'in_progress': return 'En cours';
      case 'resolved': return 'Résolu';
      case 'closed': return 'Fermé';
      default: return status;
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'low': return 'Basse';
      case 'medium': return 'Moyenne';
      case 'high': return 'Haute';
      case 'urgent': return 'Urgente';
      default: return priority;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'technical': return 'Technique';
      case 'billing': return 'Facturation';
      case 'dispute': return 'Litige';
      case 'question': return 'Question';
      case 'other': return 'Autre';
      default: return category;
    }
  };

  const getKYCStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getKYCStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'id_card': return 'Carte d\'identité';
      case 'passport': return 'Passeport';
      case 'driving_license': return 'Permis de conduire';
      case 'utility_bill': return 'Facture d\'énergie';
      default: return type;
    }
  };

  const getNotificationTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <Email />;
      case 'sms': return <Phone />;
      case 'push': return <Notifications />;
      case 'in_app': return <Info />;
      default: return <Info />;
    }
  };

  const getNotificationStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'info';
      case 'delivered': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const openTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
  };

  const closeTicket = () => {
    setSelectedTicket(null);
  };

  const openReplyDialog = () => {
    setReplyDialog(true);
  };

  const closeReplyDialog = () => {
    setReplyDialog(false);
    setReplyMessage('');
  };

  const sendReply = () => {
    if (selectedTicket && replyMessage.trim()) {
      const newMessage = {
        id: Date.now(),
        sender: 'Admin Support',
        message: replyMessage,
        timestamp: new Date().toISOString(),
        isAdmin: true,
      };

      const updatedTicket = {
        ...selectedTicket,
        messages: [...selectedTicket.messages, newMessage],
        status: 'in_progress' as const,
        updatedAt: new Date().toISOString(),
      };

      setTickets(tickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      setSelectedTicket(updatedTicket);
      closeReplyDialog();
    }
  };

  const resolveTicket = () => {
    if (selectedTicket) {
      const updatedTicket = {
        ...selectedTicket,
        status: 'resolved' as const,
        updatedAt: new Date().toISOString(),
      };

      setTickets(tickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      setSelectedTicket(updatedTicket);
    }
  };

  const approveKYC = (kycId: number) => {
    setKycRequests(kycRequests.map(k => 
      k.id === kycId 
        ? { ...k, status: 'approved' as const, reviewedAt: new Date().toISOString(), reviewer: 'Admin Support' }
        : k
    ));
  };

  const rejectKYC = (kycId: number) => {
    setKycRequests(kycRequests.map(k => 
      k.id === kycId 
        ? { ...k, status: 'rejected' as const, reviewedAt: new Date().toISOString(), reviewer: 'Admin Support' }
        : k
    ));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Support & Conformité</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setNewTicketDialog(true)}>
          Nouveau ticket
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="support tabs">
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment />
                Tickets ({tickets.filter(t => t.status !== 'closed').length})
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedUser />
                KYC ({kycRequests.filter(k => k.status === 'pending').length})
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Notifications />
                Notifications ({notifications.length})
              </Box>
            } 
          />
        </Tabs>

        {/* Tickets Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Tickets de support</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setNewTicketDialog(true)}
            >
              Nouveau ticket
            </Button>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr' }, gap: 3 }}>
            {tickets.filter(t => t.status !== 'closed').map((ticket) => (
              <Card key={ticket.id} sx={{ cursor: 'pointer' }} onClick={() => openTicket(ticket)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {ticket.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {ticket.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getPriorityLabel(ticket.priority)}
                        color={getPriorityColor(ticket.priority) as any}
                        size="small"
                      />
                      <Chip
                        label={getStatusLabel(ticket.status)}
                        color={getStatusColor(ticket.status) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{ticket.requester}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {ticket.requesterEmail}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        {ticket.messages.length} messages
                      </Typography>
                      <IconButton size="small">
                        <Reply />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        {/* KYC Requests Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Demandes de vérification d'identité
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {kycRequests.map((kyc) => (
              <Card key={kyc.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {kyc.userName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {kyc.userEmail}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Document: {getDocumentTypeLabel(kyc.documentType)}
                      </Typography>
                    </Box>
                    <Chip 
                      label={getKYCStatusLabel(kyc.status)} 
                      color={getKYCStatusColor(kyc.status) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                    Soumis le: {new Date(kyc.submittedAt).toLocaleDateString('fr-FR')}
                  </Typography>

                  {kyc.status === 'pending' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        color="success" 
                        size="small"
                        onClick={() => approveKYC(kyc.id)}
                      >
                        Approuver
                      </Button>
                      <Button 
                        variant="contained" 
                        color="error" 
                        size="small"
                        onClick={() => rejectKYC(kyc.id)}
                      >
                        Rejeter
                      </Button>
                    </Box>
                  )}

                  {kyc.notes && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {kyc.notes}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Historique des notifications
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {notifications.map((notification) => (
              <Card key={notification.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {notification.subject}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {notification.content}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getNotificationTypeIcon(notification.type)}
                      <Chip
                        label={notification.status}
                        color={getNotificationStatusColor(notification.status) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                    Destinataire: {notification.recipient}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    Envoyé le: {new Date(notification.sentAt).toLocaleDateString('fr-FR')}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>
      </Paper>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onClose={closeTicket} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedTicket.title}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={getStatusLabel(selectedTicket.status)} 
                    color={getStatusColor(selectedTicket.status) as any}
                  />
                  <Chip 
                    label={getPriorityLabel(selectedTicket.priority)} 
                    color={getPriorityColor(selectedTicket.priority) as any}
                  />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Description:</strong> {selectedTicket.description}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>Demandeur:</strong> {selectedTicket.requester} ({selectedTicket.requesterEmail})
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>Catégorie:</strong> {getCategoryLabel(selectedTicket.category)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Créé le:</strong> {new Date(selectedTicket.createdAt).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>
                Messages
              </Typography>
              
              <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                {selectedTicket.messages.map((message) => (
                  <Box 
                    key={message.id} 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      backgroundColor: message.isAdmin ? '#f5f5f5' : '#e3f2fd',
                      borderRadius: 1,
                      borderLeft: `4px solid ${message.isAdmin ? '#2196f3' : '#4caf50'}`
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {message.sender}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(message.timestamp).toLocaleString('fr-FR')}
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      {message.message}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {selectedTicket.status !== 'resolved' && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="contained" onClick={openReplyDialog}>
                    Répondre
                  </Button>
                  <Button variant="outlined" color="success" onClick={resolveTicket}>
                    Marquer comme résolu
                  </Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeTicket}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialog} onClose={closeReplyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Répondre au ticket</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReplyDialog}>Annuler</Button>
          <Button onClick={sendReply} variant="contained">
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Ticket Dialog */}
      <Dialog open={newTicketDialog} onClose={() => setNewTicketDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau ticket de support</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Titre"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select label="Catégorie">
              <MenuItem value="technical">Technique</MenuItem>
              <MenuItem value="billing">Facturation</MenuItem>
              <MenuItem value="dispute">Litige</MenuItem>
              <MenuItem value="question">Question</MenuItem>
              <MenuItem value="other">Autre</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Priorité</InputLabel>
            <Select label="Priorité">
              <MenuItem value="low">Basse</MenuItem>
              <MenuItem value="medium">Moyenne</MenuItem>
              <MenuItem value="high">Haute</MenuItem>
              <MenuItem value="urgent">Urgente</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTicketDialog(false)}>Annuler</Button>
          <Button variant="contained">
            Créer le ticket
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportScreen;
